import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { Star } from '@mui/icons-material';
import { useTranslation } from '../../hooks/useTranslation';

// Animated Counter Component
const AnimatedCounter = ({ end, duration = 2000, suffix = '', isVisible }) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!isVisible) return;
    
    const startTime = Date.now();
    const endValue = parseInt(end.replace(/\D/g, ''));
    
    const timer = setInterval(() => {
      const now = Date.now();
      const progress = Math.min((now - startTime) / duration, 1);
      
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      const currentCount = Math.floor(easeOutQuart * endValue);
      
      setCount(currentCount);
      
      if (progress === 1) {
        clearInterval(timer);
        setCount(endValue);
      }
    }, 16);
    
    return () => clearInterval(timer);
  }, [end, duration, isVisible]);
  
  return (
    <span>
      {count.toLocaleString()}{suffix}
    </span>
  );
};

const TestimonialsSection = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { t } = useTranslation();
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  // Intersection Observer for stats animation
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStatsVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (statsRef.current) {
      observer.observe(statsRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const testimonials = [
    {
      text: t('testimonials.testimonial1.text'),
      author: t('testimonials.testimonial1.author'),
      role: t('testimonials.testimonial1.role'),
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    },
    {
      text: t('testimonials.testimonial2.text'),
      author: t('testimonials.testimonial2.author'),
      role: t('testimonials.testimonial2.role'),
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    },
    {
      text: t('testimonials.testimonial3.text'),
      author: t('testimonials.testimonial3.author'),
      role: t('testimonials.testimonial3.role'),
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    },
    {
      text: t('testimonials.testimonial4.text'),
      author: t('testimonials.testimonial4.author'),
      role: t('testimonials.testimonial4.role'),
      avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80'
    }
  ];

  const renderStars = () => {
    return Array.from({ length: 5 }, (_, index) => (
      <Star key={index} sx={{ color: '#FFD700', fontSize: 20 }} />
    ));
  };

  return (
    <Box
      sx={{
        py: { xs: 4, md: 6 },
        backgroundColor: 'white',
        position: 'relative'
      }}
    >
      <Container maxWidth="lg">
        {/* Header */}
        <Box textAlign="center" mb={{ xs: 4, md: 6 }}>
          <Typography
            variant="h2"
            component="h2"
            gutterBottom
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.8rem', sm: '2.2rem', md: '2.5rem' },
              color: '#212121',
              mb: 2
            }}
          >
            {t('testimonials.title')}
          </Typography>
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: '1rem', md: '1.1rem' },
              color: '#666',
              maxWidth: 600,
              mx: 'auto',
              fontWeight: 400
            }}
          >
            {t('testimonials.subtitle')}
          </Typography>
        </Box>

        {/* Testimonials Grid */}
        <Grid container spacing={{ xs: 3, md: 4 }} mb={{ xs: 4, md: 6 }}>
          {testimonials.map((testimonial, index) => (
            <Grid size={{ xs: 12, sm: 6, md: 6 }} key={index}>
              <Card
                sx={{
                  height: '100%',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.1)'
                  },
                  border: '1px solid #f0f0f0',
                  borderRadius: 3,
                  position: 'relative',
                  overflow: 'visible'
                }}
              >
                <CardContent
                  sx={{
                    p: { xs: 3, md: 4 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  {/* Stars */}
                  <Box display="flex" mb={2} mt={1}>
                    {renderStars()}
                  </Box>

                  {/* Testimonial Text */}
                  <Typography
                    variant="body1"
                    sx={{
                      color: '#333',
                      lineHeight: 1.7,
                      fontSize: { xs: '1rem', md: '1.1rem' },
                      fontStyle: 'italic',
                      flexGrow: 1,
                      mb: 3
                    }}
                  >
                    "{testimonial.text}"
                  </Typography>

                  {/* Author Info */}
                  <Box display="flex" alignItems="center" mt="auto">
                    <Avatar
                      src={testimonial.avatar}
                      sx={{
                        width: 50,
                        height: 50,
                        mr: 2,
                        border: '2px solid #424242'
                      }}
                    />
                    <Box>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 600,
                          color: '#212121',
                          fontSize: '1rem'
                        }}
                      >
                        {testimonial.author}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#666',
                          fontSize: '0.9rem'
                        }}
                      >
                        {testimonial.role}
                      </Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Bottom Stats */}
        <Box
          ref={statsRef}
          textAlign="center"
          mt={8}
          sx={{
            p: 4,
            backgroundColor: '#f8f9fa',
            borderRadius: 3
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-around',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: { xs: 2, md: 3 }
            }}
          >
            <Box textAlign="center">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#424242',
                  mb: 1
                }}
              >
                <AnimatedCounter
                  end={t('testimonials.stats.satisfaction')}
                  suffix="%"
                  isVisible={statsVisible}
                />
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontWeight: 500
                }}
              >
                {t('testimonials.stats.satisfactionLabel')}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#424242',
                  mb: 1
                }}
              >
                <AnimatedCounter
                  end={t('testimonials.stats.salons')}
                  suffix="+"
                  isVisible={statsVisible}
                />
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontWeight: 500
                }}
              >
                {t('testimonials.stats.salonsLabel')}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#424242',
                  mb: 1
                }}
              >
                <AnimatedCounter
                  end={t('testimonials.stats.bookings')}
                  suffix="+"
                  isVisible={statsVisible}
                />
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontWeight: 500
                }}
              >
                {t('testimonials.stats.bookingsLabel')}
              </Typography>
            </Box>
            <Box textAlign="center">
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 700,
                  color: '#8B5CF6',
                  mb: 1
                }}
              >
                <AnimatedCounter
                  end={t('testimonials.stats.countries')}
                  suffix="+"
                  isVisible={statsVisible}
                />
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: '#666',
                  fontWeight: 500
                }}
              >
                {t('testimonials.stats.countriesLabel')}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Container>
    </Box>
  );
};

export default TestimonialsSection;